import {
  ArrowLeft,
  Check,
  ChevronRight,
  CpuChip01,
  Lightbulb01,
  RefreshCw05,
  Users01,
} from '@untitledui/icons';
import { Link } from 'react-router-dom';

import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { AlertFloating } from '@/lib/base/overlays/alerts';
import { humanize } from '@/utils/text';

import { useAuthInfo } from '../../allauth_auth/hooks.js';
import { useOrg } from '../../contexts/OrgContext';
import { useLearnedContext } from '../../hooks/useLearnedContext';
import { useTitle } from '../../hooks/useTitle';
import type { LearningType } from '../../types/learning';

const PROFILE_TYPE_LABELS: Record<string, string> = {
  writing_style: 'Writing style',
  user_context: 'User context',
  communication_patterns: 'Communication patterns',
  business_context: 'Business context',
  expertise_areas: 'Expertise areas',
};

const TEAM_CONTEXT_CATEGORY_LABELS: Record<string, string> = {
  business_domain: 'Business domain',
  industry: 'Industry',
  products_services: 'Products/services',
  processes: 'Processes',
  terminology: 'Terminology',
};

// Statuses ConversationState.status_display can never move on from — matches
// the backend's has_active_learning definition (learning.py:224-226/:247).
// Anything else (including an unmapped legacy/retired-agent passthrough
// value) is treated as non-terminal, i.e. still active.
const TERMINAL_LEARNING_STATUSES = new Set(['Completed', 'Failed']);

// TODO(copy): three of these labels ('Active', 'Waiting on User Input',
// 'Waiting on Schedule') are outside the closed state vocabulary
// (ahoy-ux-copy SKILL.md:154) and need copy sign-off.
// Coupled to ConversationState.ACTIVE.label et al. (apps/reasoning/models/core.py:11) —
// the backend's English strings are this map's source of truth. Brittle
// against label edits/i18n; an accepted cost of the no-wire-change decision
// for this fix.
const LEARNING_STATUS_LABELS: Record<string, string> = {
  Active: 'Active',
  'Waiting on user input': 'Waiting on User Input',
  'Waiting on schedule': 'Waiting on Schedule',
  Completed: 'Completed',
  Failed: 'Failed',
};

export default function LearnedContext() {
  const { currentOrg } = useOrg();
  const { user } = useAuthInfo();
  useTitle('Learned Context');

  const {
    loading,
    error,
    learningStatus,
    triggeringLearning,
    expandedProfile,
    setExpandedProfile,
    loadLearningData,
    handleTriggerLearning,
  } = useLearnedContext({
    orgId: currentOrg?.id,
    userId: user?.id,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Learning pattern values have varying structures
  const renderPatternValue = (key: string, value: any) => {
    if (Array.isArray(value)) {
      return (
        <div>
          <span className='text-xs text-tertiary uppercase tracking-wide'>
            {key}:
          </span>
          <ul className='mt-1 space-y-1'>
            {value.map(item => (
              <li
                key={`${key}-${String(item)}`}
                className='text-sm text-primary bg-bg-secondary px-2 py-1 rounded'
              >
                "{item}"
              </li>
            ))}
          </ul>
        </div>
      );
    } else if (typeof value === 'object' && value !== null) {
      return (
        <div>
          <span className='text-xs text-tertiary uppercase tracking-wide'>
            {key}:
          </span>
          <div className='mt-1 space-y-1'>
            {Object.entries(value).map(([subKey, subValue]) => (
              <div key={subKey} className='text-sm'>
                <span className='font-medium text-secondary'>{subKey}:</span>{' '}
                <span className='text-primary'>{String(subValue)}</span>
              </div>
            ))}
          </div>
        </div>
      );
    } else {
      return (
        <div>
          <span className='text-xs text-tertiary uppercase tracking-wide'>
            {key}:
          </span>
          <p className='text-sm text-primary mt-1'>{String(value)}</p>
        </div>
      );
    }
  };

  if (!currentOrg) {
    return (
      <div className='max-w-4xl mx-auto py-6'>
        <AlertFloating
          color='warning'
          title='Please select a team to view learned context.'
          description=''
        />
      </div>
    );
  }

  return (
    <div className='max-w-6xl mx-auto space-y-6 py-6 px-6'>
      {/* Page Header */}
      <div className='mb-8'>
        <div className='flex items-center gap-4 mb-4'>
          <Link to={`/${currentOrg.id}/settings/account`}>
            <Button color='secondary' size='sm' iconLeading={ArrowLeft}>
              Back to settings
            </Button>
          </Link>
        </div>
        <div className='flex items-center gap-3 mb-2'>
          <FeaturedIcon
            icon={CpuChip01}
            size='md'
            theme='modern'
            color='brand'
          />
          <h1 className='text-3xl font-bold text-primary'>Learned Context</h1>
        </div>
        <p className='text-tertiary'>
          AI-learned patterns and context from your communications and
          interactions
        </p>
      </div>

      {error && <AlertFloating color='error' title={error} description='' />}

      {loading ? (
        <div className='text-center py-12'>
          <LoadingIndicator size='lg' />
          <p className='text-sm text-tertiary mt-4'>Loading learning data…</p>
        </div>
      ) : (
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
          {/* Left Column - Controls */}
          <div className='space-y-6'>
            {/* Trigger Learning */}
            <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'>
              <div className='px-6 py-5 border-b border-border-secondary'>
                <div className='flex items-center gap-3'>
                  <FeaturedIcon
                    icon={Lightbulb01}
                    size='sm'
                    theme='modern'
                    color='brand'
                  />
                  <div>
                    <h2 className='text-lg font-semibold text-secondary'>
                      Trigger learning
                    </h2>
                    <p className='text-sm text-tertiary mt-0.5'>
                      Manually start AI analysis
                    </p>
                  </div>
                </div>
              </div>
              <div className='p-6'>
                <div className='grid grid-cols-1 gap-3'>
                  <Button
                    onClick={() =>
                      handleTriggerLearning(['writing_style'] as LearningType[])
                    }
                    isDisabled={triggeringLearning}
                    color='secondary'
                    size='md'
                    className='justify-start'
                  >
                    {triggeringLearning ? 'Triggering…' : 'Writing style'}
                  </Button>
                  <Button
                    onClick={() =>
                      handleTriggerLearning(['user_context'] as LearningType[])
                    }
                    isDisabled={triggeringLearning}
                    color='secondary'
                    size='md'
                    className='justify-start'
                  >
                    User context
                  </Button>
                  <Button
                    onClick={() =>
                      handleTriggerLearning([
                        'communication_patterns',
                      ] as LearningType[])
                    }
                    isDisabled={triggeringLearning}
                    color='secondary'
                    size='md'
                    className='justify-start'
                  >
                    Communication
                  </Button>
                  <Button
                    onClick={() =>
                      handleTriggerLearning([
                        'business_context',
                      ] as LearningType[])
                    }
                    isDisabled={triggeringLearning}
                    color='secondary'
                    size='md'
                    className='justify-start'
                  >
                    Business context
                  </Button>
                </div>
              </div>
            </div>

            {/* Active Learning Agents */}
            {learningStatus?.learning_agents &&
              learningStatus.learning_agents.length > 0 && (
                <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'>
                  <div className='px-6 py-5 border-b border-border-secondary'>
                    <div className='flex items-center gap-3'>
                      <FeaturedIcon
                        icon={Users01}
                        size='sm'
                        theme='modern'
                        color='brand'
                      />
                      <div>
                        <h2 className='text-lg font-semibold text-secondary'>
                          Active agents
                        </h2>
                        <p className='text-sm text-tertiary mt-0.5'>
                          Running AI learning processes
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className='p-6'>
                    <div className='space-y-3'>
                      {learningStatus.learning_agents.map(agent => (
                        <div
                          key={agent.agent_id}
                          className='flex items-center justify-between p-3 bg-bg-secondary rounded-lg'
                        >
                          <div>
                            <span className='font-mono text-sm font-medium text-primary'>
                              {agent.learning_type}
                            </span>
                            <p className='text-xs text-tertiary'>
                              Agent {agent.agent_id}
                            </p>
                          </div>
                          <Badge
                            color={
                              !TERMINAL_LEARNING_STATUSES.has(agent.status)
                                ? 'success'
                                : 'gray'
                            }
                            size='sm'
                          >
                            {/* Unmapped values fall back to 'Active' — consistent with
                                the non-terminal color treatment above. */}
                            {LEARNING_STATUS_LABELS[agent.status] ?? 'Active'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

            {/* Refresh */}
            <Button
              onClick={loadLearningData}
              isDisabled={loading}
              color='secondary'
              size='md'
              className='w-full'
              iconLeading={RefreshCw05}
            >
              {loading ? 'Refreshing…' : 'Refresh data'}
            </Button>
          </div>

          {/* Right Column - Learned Profiles & Team Context */}
          <div className='lg:col-span-2'>
            {/* User-Level Learned Profiles */}
            {learningStatus?.learned_profiles &&
              learningStatus.learned_profiles.length > 0 && (
                <div className='space-y-6 mb-8'>
                  <div className='flex items-center justify-between'>
                    <h2 className='text-xl font-semibold text-primary'>
                      Learned profiles (user-level)
                    </h2>
                    <Badge color='success' size='sm'>
                      {learningStatus.learned_profiles.length} profile
                      {learningStatus.learned_profiles.length !== 1 ? 's' : ''}
                    </Badge>
                  </div>

                  {learningStatus.learned_profiles.map(profile => (
                    <div
                      key={profile.profile_type}
                      className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'
                    >
                      <div className='px-6 py-5 border-b border-border-secondary'>
                        <div className='flex items-center justify-between'>
                          <div className='flex items-center gap-4'>
                            <h3 className='text-lg font-semibold text-secondary'>
                              {PROFILE_TYPE_LABELS[profile.profile_type] ??
                                humanize(profile.profile_type)}
                            </h3>
                            <Badge color='success' size='sm'>
                              {Math.round(profile.confidence_score * 100)}%
                              confidence
                            </Badge>
                          </div>
                          <div className='text-right'>
                            <p className='text-xs text-tertiary'>
                              {profile.sample_count} samples analyzed
                            </p>
                            <p className='text-xs text-tertiary'>
                              {new Date(
                                profile.last_analyzed
                              ).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className='p-6'>
                        {/* Analysis Summary */}
                        {profile.learned_patterns?.analysis_summary && (
                          <div className='mb-6 p-4 bg-secondary border border-secondary rounded-lg'>
                            <h4 className='font-semibold text-primary mb-2'>
                              Analysis summary
                            </h4>
                            <p className='text-sm text-secondary'>
                              {profile.learned_patterns.analysis_summary}
                            </p>
                          </div>
                        )}

                        {/* Key Characteristics */}
                        {profile.learned_patterns?.key_characteristics && (
                          <div className='mb-6'>
                            <h4 className='font-semibold text-primary mb-3'>
                              Key characteristics
                            </h4>
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                              {profile.learned_patterns.key_characteristics.map(
                                (characteristic: string) => (
                                  <div
                                    key={characteristic}
                                    className='flex items-center gap-2 p-2 bg-utility-success-50 rounded-lg'
                                  >
                                    <Check className='w-4 h-4 text-utility-success-700 flex-shrink-0' />
                                    <span className='text-sm text-utility-success-700'>
                                      {characteristic}
                                    </span>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}

                        {/* Pattern Details */}
                        <div className='space-y-4'>
                          {Object.entries(profile.learned_patterns || {})
                            .filter(
                              ([key]) =>
                                ![
                                  'analysis_summary',
                                  'key_characteristics',
                                ].includes(key)
                            )
                            .map(([key, value]) => (
                              <div
                                key={key}
                                className='p-4 bg-bg-secondary rounded-lg'
                              >
                                {renderPatternValue(key, value)}
                              </div>
                            ))}
                        </div>

                        {/* Raw JSON Toggle */}
                        <div className='mt-6 pt-6 border-t border-border-secondary'>
                          <Button
                            onClick={() =>
                              setExpandedProfile(
                                expandedProfile === profile.profile_type
                                  ? null
                                  : profile.profile_type
                              )
                            }
                            color='secondary'
                            size='sm'
                          >
                            {expandedProfile === profile.profile_type
                              ? 'Hide'
                              : 'Show'}{' '}
                            Raw JSON
                          </Button>

                          {expandedProfile === profile.profile_type && (
                            <div className='mt-4 bg-bg-primary rounded border border-border-secondary p-4'>
                              <pre className='text-xs text-secondary whitespace-pre-wrap overflow-x-auto'>
                                {JSON.stringify(
                                  profile.learned_patterns,
                                  null,
                                  2
                                )}
                              </pre>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

            {/* Team-Level Context Knowledge */}
            {learningStatus?.team_context_knowledge &&
              learningStatus.team_context_knowledge.length > 0 && (
                <div className='space-y-6'>
                  <div className='flex items-center justify-between'>
                    <h2 className='text-xl font-semibold text-primary'>
                      Team context knowledge (team-level)
                    </h2>
                    <Badge color='brand' size='sm'>
                      {learningStatus.team_context_knowledge.length} categor
                      {learningStatus.team_context_knowledge.length !== 1
                        ? 'ies'
                        : 'y'}
                    </Badge>
                  </div>

                  {learningStatus.team_context_knowledge.map(knowledge => (
                    <div
                      key={knowledge.category}
                      className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'
                    >
                      <div className='px-6 py-5 border-b border-border-secondary'>
                        <div className='flex items-center justify-between'>
                          <div className='flex items-center gap-4'>
                            <h3 className='text-lg font-semibold text-secondary'>
                              {TEAM_CONTEXT_CATEGORY_LABELS[
                                knowledge.category
                              ] ?? humanize(knowledge.category)}
                            </h3>
                            <Badge color='brand' size='sm'>
                              {Math.round(knowledge.confidence_score * 100)}%
                              confidence
                            </Badge>
                          </div>
                          <div className='text-right'>
                            <p className='text-xs text-tertiary'>
                              {new Date(
                                knowledge.last_updated
                              ).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className='p-6'>
                        {/* Summary */}
                        {knowledge.knowledge_data?.summary && (
                          <div className='mb-6 p-4 bg-secondary border border-secondary rounded-lg'>
                            <h4 className='font-semibold text-primary mb-2'>
                              Summary
                            </h4>
                            <p className='text-sm text-secondary'>
                              {knowledge.knowledge_data.summary}
                            </p>
                          </div>
                        )}

                        {/* Key Findings */}
                        {knowledge.knowledge_data?.key_findings && (
                          <div className='mb-6'>
                            <h4 className='font-semibold text-primary mb-3'>
                              Key findings
                            </h4>
                            <div className='space-y-2'>
                              {knowledge.knowledge_data.key_findings.map(
                                (finding: string) => (
                                  <div
                                    key={finding}
                                    className='flex items-start gap-2 p-2 bg-secondary rounded-lg'
                                  >
                                    <ChevronRight className='w-4 h-4 text-tertiary flex-shrink-0 mt-0.5' />
                                    <span className='text-sm text-secondary'>
                                      {finding}
                                    </span>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}

                        {/* Evidence */}
                        {knowledge.knowledge_data?.evidence && (
                          <div className='mb-4'>
                            <h4 className='font-semibold text-primary mb-3'>
                              Supporting evidence
                            </h4>
                            <div className='space-y-2'>
                              {knowledge.knowledge_data.evidence.map(
                                (item: string) => (
                                  <div
                                    key={item}
                                    className='p-2 bg-bg-secondary rounded-lg text-sm text-secondary'
                                  >
                                    {item}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}

                        {/* Sources */}
                        {knowledge.learned_from_sources.length > 0 && (
                          <div className='mt-4 pt-4 border-t border-border-secondary'>
                            <p className='text-xs text-tertiary'>
                              Sources:{' '}
                              {knowledge.learned_from_sources.join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

            {/* No learned context fallback */}
            {(!learningStatus?.learned_profiles ||
              learningStatus.learned_profiles.length === 0) &&
              (!learningStatus?.team_context_knowledge ||
                learningStatus.team_context_knowledge.length === 0) && (
                <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'>
                  <div className='p-12 text-center'>
                    <FeaturedIcon
                      icon={Lightbulb01}
                      color='gray'
                      theme='modern'
                      size='xl'
                      className='mx-auto mb-6'
                    />
                    <h3 className='text-lg font-medium text-primary mb-2'>
                      No learned context yet
                    </h3>
                    <p className='text-sm text-tertiary mb-6'>
                      Trigger learning to start analyzing your communication
                      patterns and context.
                    </p>
                    <div className='flex justify-center'>
                      <Button
                        onClick={() =>
                          handleTriggerLearning([
                            'writing_style',
                          ] as LearningType[])
                        }
                        isDisabled={triggeringLearning}
                        color='primary'
                        size='md'
                      >
                        Start with writing style
                      </Button>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
